import { useMemo } from 'react';
import type { WindowWithOptimizely } from './types';
import type { Experiments } from './experiment';
import { EXPERIMENTS } from './experiment';
import { useForcedOptimizelyExperimentQueryMap } from '@/optimizely/useForcedOptimizelyExperimentQueryMap';

declare let window: WindowWithOptimizely;

export const FORCED_EXPERIMENT_KEY_PREFIX = 'forced_experiment_';

const useOptimizelyExperimentVariation = <
  EXP_NAME extends keyof Experiments,
  VAR_NAME extends keyof Experiments[EXP_NAME]['variations'],
>(
  name: EXP_NAME,
  variation: VAR_NAME,
): boolean => {
  const EXPERIMENT = EXPERIMENTS[name] as Experiments[EXP_NAME];
  const VARIATIONS = EXPERIMENT.variations as Experiments[EXP_NAME]['variations'];
  const VARIATION = VARIATIONS[variation];

  const forcedQueryMap = useForcedOptimizelyExperimentQueryMap();
  const forcedFromQuery = forcedQueryMap[EXPERIMENT.id];

  const forcedFromStorage = useMemo(() => {
    try {
      return localStorage.getItem(`${FORCED_EXPERIMENT_KEY_PREFIX}${EXPERIMENT.id}`);
    } catch {
      // localStorage can throw in private browsing modes - treat as "not forced".
      return undefined;
    }
  }, [EXPERIMENT.id]);

  const isEnabledFromOptimizely = useMemo(() => {
    try {
      const experimentState = window?.optimizely?.get('state')?.getExperimentStates()?.[EXPERIMENT.id];
      return (
        experimentState &&
        experimentState.isActive &&
        !experimentState.isInExperimentHoldback &&
        experimentState.variation.id === VARIATION
      );
    } catch {
      // Optimizely snippet may not be loaded - treat as "not in variation".
      return undefined;
    }
  }, [EXPERIMENT.id, VARIATION]);

  if (forcedFromQuery) {
    return forcedFromQuery === VARIATION;
  }

  if (forcedFromStorage) {
    return forcedFromStorage === VARIATION;
  }

  return Boolean(isEnabledFromOptimizely);
};

export default useOptimizelyExperimentVariation;
